import { authenticatedFetch } from "./http";
import type { CreatePutawayWorkQueue, HandlingEquipment, PutawayQueueCandidate, PutawayRequest, PutawaySimulation, PutawayTask, PutawayTour, PutawayWorkQueue, SaveHandlingEquipment } from "./models";

export function loadPutawayRequests(): Promise<PutawayRequest[]> {
  return authenticatedFetch<PutawayRequest[]>("/api/v1/putaway/requests");
}

export function simulatePutaway(requestId: string): Promise<PutawaySimulation> {
  return authenticatedFetch<PutawaySimulation>(
    `/api/v1/putaway/requests/${requestId}/simulate`,
    { method: "POST" }
  );
}

export function acceptPutaway(
  requestId: string, decisionId: string, version: number
): Promise<PutawayTask> {
  return authenticatedFetch<PutawayTask>(
    `/api/v1/putaway/requests/${requestId}/accept`,
    {
      method: "POST",
      body: JSON.stringify({ decisionId, version })
    }
  );
}

export function loadPutawayTasks(): Promise<PutawayTask[]> {
  return authenticatedFetch<PutawayTask[]>("/api/v1/putaway/tasks");
}

export function loadPutawayWorkQueues(): Promise<PutawayWorkQueue[]> {
  return authenticatedFetch<PutawayWorkQueue[]>("/api/v1/putaway/work-queues");
}

export function loadAllPutawayWorkQueues(): Promise<PutawayWorkQueue[]> {
  return authenticatedFetch<PutawayWorkQueue[]>(
    "/api/v1/putaway/work-queues?includeInactive=true"
  );
}

export function createPutawayWorkQueue(
  request: CreatePutawayWorkQueue
): Promise<PutawayWorkQueue> {
  return authenticatedFetch<PutawayWorkQueue>("/api/v1/putaway/work-queues", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function changePutawayWorkQueueStatus(
  queue: PutawayWorkQueue, status: PutawayWorkQueue["status"]
): Promise<PutawayWorkQueue> {
  return authenticatedFetch<PutawayWorkQueue>(
    `/api/v1/putaway/work-queues/${queue.id}/status`, {
      method: "PUT", body: JSON.stringify({ status, version: queue.version })
    }
  );
}

export function deletePutawayWorkQueue(queueId: string): Promise<void> {
  return authenticatedFetch<void>(`/api/v1/putaway/work-queues/${queueId}`, {
    method: "DELETE"
  });
}

export function loadHandlingEquipment(): Promise<HandlingEquipment[]> {
  return authenticatedFetch<HandlingEquipment[]>("/api/v1/handling-equipment");
}

export function returnHandlingEquipmentToPark(
  equipmentNumber: string
): Promise<HandlingEquipment> {
  return authenticatedFetch<HandlingEquipment>(
    "/api/v1/handling-equipment/return-to-park",
    { method: "POST", body: JSON.stringify({ equipmentNumber }) }
  );
}

export function createHandlingEquipment(
  request: SaveHandlingEquipment
): Promise<HandlingEquipment> {
  return authenticatedFetch<HandlingEquipment>("/api/v1/handling-equipment", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function updateHandlingEquipment(
  equipment: HandlingEquipment, request: SaveHandlingEquipment
): Promise<HandlingEquipment> {
  return authenticatedFetch<HandlingEquipment>(
    `/api/v1/handling-equipment/${equipment.id}`,
    { method: "PUT", body: JSON.stringify({ ...request, version: equipment.version }) }
  );
}

export function deleteHandlingEquipment(equipmentId: string): Promise<void> {
  return authenticatedFetch<void>(`/api/v1/handling-equipment/${equipmentId}`, {
    method: "DELETE"
  });
}

export function loadPutawayQueueCandidates(
  queueId: string
): Promise<PutawayQueueCandidate[]> {
  return authenticatedFetch<PutawayQueueCandidate[]>(
    `/api/v1/putaway/work-queues/${queueId}/candidates`
  );
}

export function loadPutawayTours(): Promise<PutawayTour[]> {
  return authenticatedFetch<PutawayTour[]>("/api/v1/putaway/tours");
}

export function createPutawayTour(
  queueId: string, taskIds: string[], direct = false,
  handlingEquipmentId: string | null = null, scanEquipment = false,
  handlingEquipmentIdentifier: string | null = null
): Promise<PutawayTour> {
  return authenticatedFetch<PutawayTour>(
    `/api/v1/putaway/work-queues/${queueId}/tours`,
    { method: "POST", body: JSON.stringify({
      taskIds, direct, handlingEquipmentId, handlingEquipmentIdentifier,
      scanEquipment
    }) }
  );
}

export interface PutawayEquipmentSelection {
  equipmentRequired: boolean;
  palletized: boolean;
  weightKg: number;
  equipment: Array<{
    id: string;
    equipmentNumber: string;
    typeCode: "PALLET_TRUCK" | "FORKLIFT";
    typeName: string;
    maxLoadKg: number | null;
  }>;
}

export function loadPutawayTaskHandlingEquipment(
  taskId: string
): Promise<PutawayEquipmentSelection> {
  return authenticatedFetch<PutawayEquipmentSelection>(
    `/api/v1/putaway/tasks/${taskId}/handling-equipment`
  );
}

export function loadPutawayTourHandlingEquipment(
  tourId: string
): Promise<PutawayEquipmentSelection["equipment"]> {
  return authenticatedFetch<PutawayEquipmentSelection["equipment"]>(
    `/api/v1/putaway/tours/${tourId}/handling-equipment`
  );
}

export function scanPutawayTourEquipment(
  tour: PutawayTour, code: string
): Promise<PutawayTour> {
  return authenticatedFetch<PutawayTour>(
    `/api/v1/putaway/tours/${tour.id}/scan-equipment`,
    { method: "POST", body: JSON.stringify({ code, version: tour.version }) }
  );
}

export function scanPutawayTourHu(
  tour: PutawayTour, code: string
): Promise<PutawayTour> {
  return authenticatedFetch<PutawayTour>(
    `/api/v1/putaway/tours/${tour.id}/scan-hu`,
    { method: "POST", body: JSON.stringify({ code, version: tour.version }) }
  );
}

export function completePutawayTourLoading(
  tour: PutawayTour
): Promise<PutawayTour> {
  return authenticatedFetch<PutawayTour>(
    `/api/v1/putaway/tours/${tour.id}/complete-loading`,
    { method: "POST", body: JSON.stringify({ version: tour.version }) }
  );
}

export function cancelPutawayTour(tour: PutawayTour): Promise<PutawayTour> {
  return authenticatedFetch<PutawayTour>(
    `/api/v1/putaway/tours/${tour.id}/cancel`,
    { method: "POST", body: JSON.stringify({ version: tour.version }) }
  );
}

export function updatePutawayWorkQueue(
  queue: PutawayWorkQueue,
  changes: Pick<PutawayWorkQueue, "name" | "groupingMode" |
    "maxHandlingUnits" | "maxWeightKg" | "maxVolumeMm3" |
    "routingStrategy" | "heavyThresholdKg">
): Promise<PutawayWorkQueue> {
  return authenticatedFetch<PutawayWorkQueue>(
    `/api/v1/putaway/work-queues/${queue.id}`,
    {
      method: "PUT",
      body: JSON.stringify({
        ...changes,
        version: queue.version
      })
    }
  );
}

function putawayTaskAction(
  taskId: string, action: string, version: number, code?: string
): Promise<PutawayTask> {
  return authenticatedFetch<PutawayTask>(
    `/api/v1/putaway/tasks/${taskId}/${action}`,
    {
      method: "POST",
      body: JSON.stringify(code === undefined ? { version } : { code, version })
    }
  );
}

export function claimPutawayTask(task: PutawayTask): Promise<PutawayTask> {
  return putawayTaskAction(task.id, "claim", task.version);
}

export function scanPutawayHu(
  task: PutawayTask, code: string
): Promise<PutawayTask> {
  return putawayTaskAction(task.id, "scan-hu", task.version, code);
}

export function scanPutawayDestination(
  task: PutawayTask, code: string
): Promise<PutawayTask> {
  return putawayTaskAction(task.id, "scan-destination", task.version, code);
}

export function completePutawayTask(task: PutawayTask): Promise<PutawayTask> {
  return putawayTaskAction(task.id, "complete", task.version);
}

export type PutawayProblemReason =
  "DESTINATION_INACCESSIBLE" | "DESTINATION_FULL" |
  "DESTINATION_BLOCKED" | "DESTINATION_DAMAGED" |
  "NO_COMPATIBLE_DESTINATION" |
  "PACKAGING_DAMAGED" | "MATERIAL_DAMAGE_SUSPECTED" |
  "PACKAGING_UNKNOWN" | "HU_DAMAGED" | "OTHER";

export interface PutawayProblemResult {
  exceptionId: string;
  cancelledTaskId: string;
  replacementTask: PutawayTask | null;
  complementaryTaskId: string | null;
  complementaryTaskType: "REPACKAGING" | "QUALITY_INSPECTION" | null;
  message: string;
}

export interface PutawayAlert {
  id: string;
  taskId: string | null;
  requestId: string;
  decisionId: string | null;
  itemId: string;
  itemCode: string;
  itemName: string;
  huNumber: string;
  clientCode: string | null;
  clientName: string | null;
  quantity: number;
  uom: string;
  sourcePointCode: string;
  packagingCode: string | null;
  packagingName: string | null;
  rejectedBinCode: string | null;
  reasonCode: PutawayProblemReason;
  comment: string;
  resolutionAction:
    "REPLAN" | "RESIMULATE" | "REPACKAGING" | "PACKAGING_IDENTIFICATION" |
    "QUALITY_INSPECTION" | "MANUAL_REVIEW";
  replacementTaskId: string | null;
  replacementBinCode: string | null;
  complementaryTaskId: string | null;
  complementaryTaskType: "REPACKAGING" | "QUALITY_INSPECTION" | null;
  complementaryTaskStatus:
    "OPEN" | "ASSIGNED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED" | null;
  status: "OPEN" | "ASSIGNED" | "RESOLVED" | "CANCELLED";
  resolutionComment: string | null;
  reportedAt: string;
  reportedBy: string | null;
  reportedByEmail: string | null;
}

export function loadPutawayAlerts(): Promise<PutawayAlert[]> {
  return authenticatedFetch<PutawayAlert[]>("/api/v1/putaway/alerts");
}

export function loadPutawayDecision(
  decisionId: string
): Promise<PutawaySimulation> {
  return authenticatedFetch<PutawaySimulation>(
    `/api/v1/putaway/decisions/${decisionId}`
  );
}

export function identifyPutawayPackaging(
  alertId: string, itemPackagingId: string, comment: string
): Promise<PutawaySimulation> {
  return authenticatedFetch<PutawaySimulation>(
    `/api/v1/putaway/alerts/${alertId}/identify-packaging`,
    {
      method: "POST",
      body: JSON.stringify({ itemPackagingId, comment })
    }
  );
}

export function resimulatePutawayAlert(
  alertId: string, releaseRejectedBin: boolean, comment: string
): Promise<PutawaySimulation> {
  return authenticatedFetch<PutawaySimulation>(
    `/api/v1/putaway/alerts/${alertId}/resimulate`,
    {
      method: "POST",
      body: JSON.stringify({ releaseRejectedBin, comment })
    }
  );
}
